import { useState, useEffect } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomizeIngredientsDialog({ product, onConfirm, onClose }) {
  const [customRecipe, setCustomRecipe] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    // Initialize with product's default recipe
    if (product.recipe) {
      setCustomRecipe([...product.recipe]);
      calculatePrice(product.recipe);
    }
  }, [product]);

  const calculatePrice = (recipe) => {
    const price = recipe.reduce((total, item) => {
      return total + (item.quantity * item.price_per_unit);
    }, 0);
    setTotalPrice(price);
  };

  const updateQuantity = (index, change) => {
    const newRecipe = [...customRecipe];
    const newQuantity = newRecipe[index].quantity + change;
    
    // Minimum 0
    if (newQuantity >= 0) {
      newRecipe[index].quantity = newQuantity;
      setCustomRecipe(newRecipe);
      calculatePrice(newRecipe);
    }
  };

  const handleConfirm = () => {
    // Filter out items with 0 quantity
    const finalRecipe = customRecipe.filter(item => item.quantity > 0);
    
    if (finalRecipe.length === 0) {
      toast.error('Minimal harus ada 1 bahan!', {
        description: 'Pilih minimal 1 bahan untuk melanjutkan',
        duration: 3000
      });
      return;
    }

    onConfirm({
      ...product,
      custom_recipe: finalRecipe,
      custom_price: totalPrice
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">{product.name}</h2>
            <p className="text-sm text-gray-500">Sesuaikan komposisi</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="space-y-4 mb-6">
            {customRecipe.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{item.name}</div>
                  <div className="text-sm text-gray-500">
                    @ Rp {item.price_per_unit.toLocaleString()} per {item.unit}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => updateQuantity(index, -1)}
                    className="p-2 rounded-full hover:bg-gray-200 disabled:opacity-50"
                    disabled={item.quantity === 0}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  
                  <div className="w-16 text-center">
                    <div className="text-lg font-bold">{item.quantity}</div>
                    <div className="text-xs text-gray-500">{item.unit}</div>
                  </div>

                  <button
                    onClick={() => updateQuantity(index, 1)}
                    className="p-2 rounded-full hover:bg-gray-200"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                <div className="w-24 text-right font-medium text-gray-900">
                  Rp {(item.quantity * item.price_per_unit).toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="border-t pt-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-lg font-medium text-gray-700">Total Harga:</span>
              <span className="text-2xl font-bold text-blue-600">
                Rp {totalPrice.toLocaleString()}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              * Harga berdasarkan komposisi yang Anda pilih
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Batal
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Tambahkan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
